import { REFERENCE_LS_KEYS, safeLocalStorage } from '@scalar/helpers/object/local-storage'
import type { OpenAPIV3_1 } from '@scalar/openapi-types'
import type { WorkspaceStore } from '@scalar/workspace-store/client'
import type { AuthStore } from '@scalar/workspace-store/entities/auth'
import { type Auth, AuthSchema } from '@scalar/workspace-store/entities/auth'
import type { SecuritySchemeObjectSecret } from '@scalar/workspace-store/request-example'
import {
  getSecurityRequirements,
  getSecuritySchemes,
  getSelectedSecurity,
  getSelectedServer,
  getServers,
  mergeSecurity,
} from '@scalar/workspace-store/request-example'
import { isOpenApiDocument } from '@scalar/workspace-store/schemas/type-guards'
import { coerceValue } from '@scalar/workspace-store/schemas/typebox-coerce'
import type { OpenApiDocument, OperationObject } from '@scalar/workspace-store/schemas/v3.1/strict/openapi-document'

export function getOperations(doc: Partial<OpenAPIV3_1.Document>) {
  return Object.values(doc.paths ?? {}).flatMap((path) => Object.values(path ?? {})) as OperationObject[]
}

/** Flattens all security requirements from a document */
function getSecurityFromDocument(
  documentName: string,
  document: OpenApiDocument,
  authStore: AuthStore,
): SecuritySchemeObjectSecret[] {
  const mergedSecurity = mergeSecurity(document?.components?.securitySchemes, {}, authStore, documentName)

  const securityRequirements = getSecurityRequirements(document.security)

  const selectedSecurity = getSelectedSecurity(
    authStore.getAuthSelectedSchemas({
      type: 'document',
      documentName,
    }),
    undefined,
    securityRequirements,
  )

  return getSecuritySchemes(mergedSecurity, selectedSecurity.selectedSchemes[selectedSecurity.selectedIndex] ?? {})
}

/** Generate document settings from workspace store. AsyncAPI docs are skipped — this feature is OpenAPI-native. */
export function createDocumentSettings(workspaceStore: WorkspaceStore) {
  const openApiEntries: [string, OpenApiDocument][] = []
  for (const [key, document] of Object.entries(workspaceStore.workspace.documents)) {
    if (isOpenApiDocument(document)) {
      openApiEntries.push([key, document])
    }
  }

  return Object.fromEntries(
    openApiEntries.map(([key, document]) => {
      const servers = getServers(document.servers, {
        documentUrl: document['x-scalar-original-source-url'],
      })

      return [
        key,
        {
          activeServer: getSelectedServer(document, null, null, servers),
          securitySchemes: getSecurityFromDocument(key, document, workspaceStore.auth),
        },
      ]
    }),
  )
}

// Local storage helper instance, safely wrapped.
const storage = safeLocalStorage()

/**
 * Provides an interface to store and retrieve authentication scheme
 * information in local storage, including both the available schemes and
 * the user's selected schemes.
 */
export const authStorage = () => {
  const getKey = (slug: string) => {
    return `${REFERENCE_LS_KEYS.AUTH}-${slug}`
  }

  return {
    /**
     * Retrieves and coerces the authentication schemes stored in local storage.
     */
    getAuth: (slug: string) => {
      const parsed = JSON.parse(storage.getItem(getKey(slug)) ?? '{}')
      return coerceValue(AuthSchema, parsed)
    },
    /**
     * Stores the authentication schemes in local storage.
     * @param value The Auth object to stringify and store.
     */
    setAuth: (slug: string, value: Auth) => {
      storage.setItem(getKey(slug), JSON.stringify(value))
    },
  }
}

/**
 * Restores authentication secrets from local storage to the workspace store.
 *
 * This function iterates through stored authentication schemes and restores
 * any secret values (keys starting with x-scalar-secret-) to the active
 * document's security schemes. It uses the current security schemes as the
 * source of truth, only restoring secrets for structures that exist in the
 * current document.
 */
export const restoreAuthSecretsFromStorage = ({
  documentName,
  workspaceStore,
}: {
  documentName: string
  workspaceStore: WorkspaceStore
}): void => {
  const authPersistence = authStorage()
  const auth = authPersistence.getAuth(documentName)
  workspaceStore.auth.load({ [documentName]: auth })
}

export function safeParseJson(value: string) {
  try {
    return JSON.parse(value)
  } catch {
    return
  }
}
